import axios from 'axios';
import { Attestation, IPv8API } from '../ipv8/IPv8API';
import { Attribute } from '../ipv8/types/Attribute';
import { Dict } from '../ipv8/types/Dict';
import { IVerifieeService } from '../ipv8/types/IVerifieeService';
import { interval } from '../ipv8/util/interval';
import { ServerDescriptor } from "../server/IAttestationServerRESTAPI";
import { AttributeDescription, ClientProcedure, Credential, PeerId } from '../types/types';
import { arrayContentsEqual } from "../util/mapEqual";
import { strlist } from '../util/strlist';
import { Validate } from '../util/validate';
import { HttpAPIGateway } from './HttpAPIGateway';

export class AttestationClient {
    public config = {
        maxAttemptsToPollStagedAttributes: 20,
        pollStagedAttributesEveryMillis: 1000,
        pollAttestationRequestEveryMillis: 1000,
        allowVerificationTimeoutMillis: 10000
    }

    constructor(
        private me: PeerId,
        private api: IPv8API,
        public verifieeService: IVerifieeService,
        private logger: (...args: any[]) => any = console.log
    ) { }

    public stop() {
        this.verifieeService.stop();
    }

    public async getServerDetails(http_address: string): Promise<ServerDescriptor> {
        return new HttpAPIGateway(axios, http_address).about();
    }

    /**
     * Run an OWAttestationProcedure
     * @param procedure The procedure description
     * @param credential_values The values of all required credentials
     */
    public async execute(
        procedure: ClientProcedure,
        credential_values: Dict<string>,
        userConsentOnData: (data: Attribute[]) => Promise<boolean> = null,
        onStatusChange: (status: Status) => any = () => { },
    ): Promise<OWResponse> {
        const { desc, server } = procedure
        try {
            this.log(`Start executing procedure ${procedure.desc.procedure_name}.`);
            this.grantVerificationOfCredentials(server.mid_b64, desc.requirements);

            onStatusChange(Status.FETCHING_LOCAL_CREDENTIALS);
            const credentials = await this.fetchLocalCredentials(desc.requirements, credential_values)

            onStatusChange(Status.FETCHING_DATA);
            const data = await this.fetchDataAtProvider(procedure, credentials)

            // Abort if user does not consent with provided data
            if (userConsentOnData && !await userConsentOnData(data)) {
                return null;
            }

            onStatusChange(Status.REQUESTING_ATTESTATION);
            const attestations = await this.requestAndAwaitAttestations(procedure)

            onStatusChange(Status.COMPLETE);
            return { data, attestations }
        } catch (err) {
            throw new Error(`OWAttest failed. Status: ${err.errno}. Message: ${err.message}`);
        }
    }

    /** Fetch the hashes from the client's IPv8 belonging to specific attributes. */
    protected async fetchLocalCredentials(
        credential_names: string[],
        values: Dict<string>
    ): Promise<Credential[]> {
        if (credential_names.length === 0) {
            return [];
        }
        this.log(`Fetching credential hashes of ${strlist(credential_names)}...`)
        const promises = credential_names.map(async cName => {
            const attribute_hash = await this.fetchCredentialHashOrThrow(cName)
            this.log(`Received hash for ${cName}: ${attribute_hash}.`)
            return {
                attribute_name: cName,
                attribute_hash,
                attribute_value: values[cName],
            }
        })
        const result = await Promise.all(promises)
        this.log(`Received all credential hashes.`)
        return result;
    }

    /** 
     * Check that we have an attestation for a given attribute.
     * @throws If an attribute hash is missing.
     */
    protected async fetchCredentialHashOrThrow(attribute_name: string): Promise<string> {
        const attestations = await this.api.listAttestations()
        const attestation = attestations.find(a => a.attribute_name === attribute_name)
        if (!attestation) {
            throw new Error(`Missing hash for attribute ${attribute_name}.`)
        }
        return attestation.attribute_hash
    }

    /**
     * Request the OWAttestationServer to stage our attributes and
     * verify our credentials if needed, passing the credential data.
     */
    protected async fetchDataAtProvider(
        procedure: ClientProcedure,
        credentials: Credential[]
    ): Promise<Attribute[]> {
        const procedure_name = procedure.desc.procedure_name;
        const request = {
            procedure_id: procedure_name,
            mid_b64: this.me.mid_b64,
            credentials
        }
        this.log(`Requesting data for procedure ${procedure_name} at provider...`);
        const response = await new HttpAPIGateway(axios, procedure.server.http_address).procedure(request);
        const data = this.validateReceivedDataOrThrow(procedure, response);
        this.log(`Received data.`, data);
        return data;
    }

    /**
     * Allow incoming verification requests from the Provider for the given attributes.
     */
    async grantVerificationOfCredentials(mid_b64: string, attributeNames: string[]) {
        if (attributeNames.length === 0) {
            return [];
        }
        this.log(`Staged verification for '${mid_b64}' of attributes ${strlist(attributeNames)}.`);
        const verificationValidUntil = Date.now() + this.config.allowVerificationTimeoutMillis
        await this.verifieeService.stageVerification(
            mid_b64,
            attributeNames,
            verificationValidUntil
        )
        this.log(`Verification complete.`);
    }

    /**
     * We don't trust the data provided by the server. Hence first validate it.
     */
    protected validateReceivedDataOrThrow(procedure: ClientProcedure, receivedData: any): Attribute[] {
        const { arrayWithEach, many, hasKey } = Validate
        const validator = arrayWithEach(many([hasKey('attribute_name'), hasKey('attribute_value')]))
        const error = validator(receivedData)
        if (error !== false) {
            throw new Error(`Server response malformed: ${error}.`);
        }
        const array: Attribute[] = receivedData

        if (!arrayContentsEqual(array.map(d => d.attribute_name), procedure.desc.attributes.map(a => a.name))) {
            throw new Error(`Server response malformed: unexpected or missing attributes.`);
        }

        return array;
    }

    protected async requestAndAwaitAttestations(procedure: ClientProcedure) {
        const { attributes: attribute_names } = procedure.desc
        const attestations: Attestation[] = []
        for (const attribute of attribute_names) {
            this.log(`Requesting attestation of ${attribute.name}...`)
            await this.requestAttestation(procedure, attribute)
            this.log(`Waiting for attestation ${attribute.name} to be completed...`)
            const attestation = await this.awaitAttestation(attribute.name)
            this.log(`Attestation of ${attribute.name} complete.`)
            attestations.push(attestation)
        }
        return attestations
    }

    /** Request the attestation */
    protected async requestAttestation(procedure: ClientProcedure, attribute: AttributeDescription) {
        return this.api.requestAttestation(procedure.server.mid_b64, attribute.name, attribute.type)
    }

    protected async awaitAllAttestations(procedure: ClientProcedure): Promise<Dict<Attestation>> {
        const { attributes } = procedure.desc
        const promises = attributes.map(attr => this.awaitAttestation(attr.name))
        return Promise.all(promises).then((results: Attestation[]) =>
            results.reduce((map, r, i) => ({ ...map, [attributes[i].name]: r }), {})
        )
    }

    /** Await the receipt of attestations */
    protected async awaitAttestation(attr_name: string): Promise<Attestation> {
        return new Promise(resolve => {
            const sub = interval(this.config.pollAttestationRequestEveryMillis).subscribe(() => {
                this.api.listAttestations().then(atts => {
                    const att = atts.find(a => a.attribute_name === attr_name)
                    if (att) {
                        sub.unsubscribe()
                        resolve(att)
                    }
                })
            })
        })
    }

    protected log(...args: any[]) {
        if (this.logger) {
            this.logger('[AttestationClient]', ...args)
        }
    }
}

export interface OWResponse {
    data: Attribute[]
    attestations: Attestation[]
}

export enum Status {
    FETCHING_LOCAL_CREDENTIALS = "FETCHING_LOCAL_CREDENTIALS",
    FETCHING_DATA = "FETCHING_DATA",
    REQUESTING_ATTESTATION = "REQUESTING_ATTESTATION",
    COMPLETE = "COMPLETE"
}
